/**
 * Minimalerei Product Gallery — Phase 3.C
 *
 * Custom Element <minimalerei-gallery>. Handles:
 *   - Thumbnail click → cross-fade to corresponding media item
 *   - Variant input change → switch to variant.featured_media
 *     (single trigger, no double-fire from product-info:loaded)
 *   - Touch swipe (left/right) on main media (mobile)
 *   - Keyboard arrow nav when a thumbnail has focus
 *   - Lazy video hydration on first activation; unload on switch
 *   - Save-data + reduced-motion: video shows poster + manual play
 *   - Image counter live update via aria-live region
 *
 * Variant matching reads embedded JSON (output by Liquid):
 *   <script type="application/json" data-variant-data>
 *     [{ id, options: ["Gold","S"], featured_media: { id } }]
 *   </script>
 * Falls back gracefully (no variant-switch) if JSON missing or
 * the matched variant has no featured_media.
 */

class MinimalereiGallery extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.counterCurrent = this.querySelector('[data-counter-current]');
    this.counterTotal = this.querySelector('[data-counter-total]');
    this.mainEl = this.querySelector('[data-gallery-main]');
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');

    if (this.items.length === 0) return;

    this.currentIndex = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.saveData = (navigator.connection && navigator.connection.saveData) || false;
    this.swipeEnabled = !this.hasAttribute('data-swipe-disabled');
    this.autoplayEnabled = !this.hasAttribute('data-autoplay-disabled');

    // Parse variant data for variant→media matching
    const variantDataEl = this.querySelector('[data-variant-data]');
    this.variantData = [];
    if (variantDataEl) {
      try {
        this.variantData = JSON.parse(variantDataEl.textContent);
      } catch (e) {
        this.variantData = [];
      }
    }

    this.bindEvents();
    this.bindVariantListener();
    this.bindMaterialPickerListener();

    // Initial filter: apply variant-based filter on page load. Two paths
    // because not every product has <variant-selects>:
    //   1. Form-based (Gravurschmuck etc.): handleVariantChange reads
    //      selected radios/selects from the form.
    //   2. Material-picker only (Kinderbild-Schmuck): fall back to the
    //      hidden product-variant-id input that Material-Picker keeps in
    //      sync. Handles ?variant= URL params too (Shopify presets it).
    const initForm = document.querySelector('product-info form, form[action*="/cart/add"]');
    if (initForm) {
      this.handleVariantChange(initForm);
    }
    const variantIdInput = document.querySelector('input[name="id"].product-variant-id');
    if (variantIdInput && variantIdInput.value) {
      this.handleVariantSwitch(variantIdInput.value);
    }

    // First item is server-rendered as is-active. Sync JS state.
    // If first item is video, don't auto-load (only on activate).
  }

  bindEvents() {
    // Thumbnail clicks
    this.thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => this.activate(idx));
    });

    // Prev / next overlay buttons (visible on main image, desktop)
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate(1));
    }

    // Keyboard arrows when any gallery descendant has focus
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.navigate(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigate(-1);
      }
    });

    // Touch swipe on main media
    this.bindSwipe();
  }

  navigate(delta) {
    const total = this.items.length;
    if (total === 0) return;
    let next = this.currentIndex;
    // Step in delta direction, skipping hidden items. Cap loop at total
    // iterations so we never spin infinitely if everything is hidden.
    for (let i = 0; i < total; i++) {
      next = (next + delta + total) % total;
      if (!this.items[next].hasAttribute('hidden')) break;
    }
    if (next !== this.currentIndex) this.activate(next);
  }

  bindSwipe() {
    if (!this.mainEl) return;
    if (!this.swipeEnabled) return;
    let startX = 0;
    let startY = 0;
    let active = false;

    // Drop pointerType filter so swipe works in:
    //   - real mobile (pointerType: 'touch')
    //   - DevTools mobile emulation (pointerType varies based on touch-toggle)
    //   - desktop mouse-drag (bonus — arrows remain primary)
    // Use setPointerCapture so pointerup fires reliably even if the
    // cursor leaves the gallery's bounding box during the drag — without
    // capture, fast swipes that exit the box would lose the up-event and
    // never trigger navigate.
    this.mainEl.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('[data-gallery-prev], [data-gallery-next]')) return;
      startX = e.clientX;
      startY = e.clientY;
      active = true;
      try { this.mainEl.setPointerCapture(e.pointerId); } catch (err) { /* IE/old */ }
    });

    this.mainEl.addEventListener('pointerup', (e) => {
      if (!active) return;
      active = false;
      try { this.mainEl.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      // Horizontal swipe: ≥50px X movement, X > Y (Y-cap prevents
      // accidental fires when user is trying to scroll vertically).
      if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy)) {
        this.navigate(dx < 0 ? 1 : -1);
      }
    });

    this.mainEl.addEventListener('pointercancel', () => { active = false; });
  }

  bindVariantListener() {
    // Find the variant-input form. On Dawn, this is form[action*="/cart/add"]
    // OR inside <product-info>. Listen for 'change' on [name^="options"].
    // Single source of truth — we deliberately do NOT also listen to
    // product-info:loaded to avoid double-trigger glitches.
    const form = document.querySelector('product-info form, form[action*="/cart/add"]');
    if (!form) return;

    form.addEventListener('change', (e) => {
      if (!e.target.matches('[name^="options"]')) return;
      this.handleVariantChange(form);
    });
  }

  bindMaterialPickerListener() {
    // Products without <variant-selects> (e.g. Kinderbild-Schmuck) don't
    // emit form-change events on variant switch — the Material-Picker
    // updates DOM directly via applyVariantUpdates. We listen for clicks
    // on material cards and extract the variant ID from data-variant-id.
    // Document-level delegation survives section re-renders.
    document.addEventListener('click', (e) => {
      const card = e.target.closest('[data-material-card]');
      if (!card) return;
      const variantId = card.dataset.variantId;
      if (variantId) {
        this.handleVariantSwitch(variantId);
      }
    });
  }

  handleVariantChange(form) {
    if (!this.variantData.length) return;

    // Read currently-selected option values from the form, in order
    // option1, option2, option3 (Shopify convention).
    const selected = [];
    form.querySelectorAll('fieldset[role="radiogroup"], select[name^="options"]').forEach((group) => {
      if (group.matches('select')) {
        selected.push(group.value);
      } else {
        const checked = group.querySelector('input[type="radio"]:checked');
        if (checked) selected.push(checked.value);
      }
    });

    if (selected.length === 0) return;

    // Find variant whose options array exactly matches selected.
    const matched = this.variantData.find((v) =>
      v.options &&
      v.options.length === selected.length &&
      v.options.every((opt, i) => opt === selected[i])
    );

    if (!matched) return;

    this.handleVariantSwitch(matched.id);
  }

  /**
   * Apply variant-based filter + auto-switch given a variant ID directly.
   * Single entry point used by both the form-change listener (variant-
   * selects path) and the material-picker click listener.
   */
  handleVariantSwitch(variantId) {
    if (!variantId) return;

    // Filter items + thumbs by variant ID. Items without data-variant-ids
    // are generic and stay visible.
    this.filterGalleryByVariant(variantId);

    // Auto-switch to variant's featured_media if defined in variantData.
    const matched = this.variantData.find((v) => String(v.id) === String(variantId));
    if (matched && matched.featured_media && matched.featured_media.id) {
      const targetId = String(matched.featured_media.id);
      const idx = this.items.findIndex((item) => item.dataset.mediaId === targetId);
      if (idx >= 0 && idx !== this.currentIndex) {
        this.activate(idx);
        return;
      }
    }

    // Fallback: if current active item is hidden after filter, jump to
    // first visible item so we never display a hidden-but-active slide.
    const currentItem = this.items[this.currentIndex];
    if (currentItem && currentItem.hasAttribute('hidden')) {
      this.activateFirstVisible();
    }
  }

  filterGalleryByVariant(variantId) {
    const filterFn = (el) => {
      const variantIdsAttr = el.getAttribute('data-variant-ids');

      // No attribute = generic image = always visible
      if (!variantIdsAttr) {
        el.removeAttribute('hidden');
        el.style.display = '';
        return;
      }

      const variantIds = variantIdsAttr.split(',').map((id) => id.trim());
      if (variantIds.includes(String(variantId))) {
        el.removeAttribute('hidden');
        el.style.display = '';
      } else {
        el.setAttribute('hidden', '');
        el.style.display = 'none';
      }
    };

    this.items.forEach(filterFn);
    this.thumbs.forEach(filterFn);

    // Recount visible items for the "X / Y" indicator
    this.updateCounter();
  }

  activateFirstVisible() {
    const idx = this.items.findIndex((item) =>
      !item.hasAttribute('hidden') && item.style.display !== 'none'
    );
    if (idx !== -1) {
      this.activate(idx);
    }
  }

  activate(index) {
    if (index < 0 || index >= this.items.length) return;
    if (index === this.currentIndex && this.activated) return;
    this.activated = true;

    const prev = this.items[this.currentIndex];
    const next = this.items[index];

    // Unload previous video if any (memory + bandwidth)
    if (prev && prev !== next) {
      this.unloadVideo(prev);
    }

    // Update active state on items
    this.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
      item.setAttribute('aria-hidden', String(i !== index));
    });

    // Update active state on thumbs
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-pressed', String(i === index));
    });

    // Scroll the active thumb fully into view in the horizontal strip.
    // block:'nearest' prevents the page from jumping vertically; inline:
    // 'nearest' moves the strip horizontally by the minimum needed.
    const activeThumb = this.thumbs[index];
    if (activeThumb) {
      activeThumb.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }

    // Hydrate video for the newly-active item if it's a video
    if (next && next.dataset.mediaType === 'video') {
      this.hydrateVideo(next);
    }

    this.currentIndex = index;
    this.updateCounter();
  }

  /**
   * Counter shows position within VISIBLE items (e.g. "3 / 7" when 1 of
   * 8 items is variant-filtered out). Updated on activate + filter.
   */
  updateCounter() {
    if (!this.counterCurrent) return;
    const visible = this.items.filter((item) => !item.hasAttribute('hidden'));
    const currentVisibleIdx = visible.indexOf(this.items[this.currentIndex]);
    this.counterCurrent.textContent = String(currentVisibleIdx >= 0 ? currentVisibleIdx + 1 : 1);
    if (this.counterTotal) {
      this.counterTotal.textContent = String(visible.length);
    }
  }

  hydrateVideo(item) {
    const slot = item.querySelector('[data-video-slot]');
    if (!slot) return;
    if (slot.querySelector('video')) return; // already hydrated

    const src = slot.dataset.videoSrc;
    if (!src) return;

    // Save-data, reduced-motion, OR auto-play disabled via setting:
    // don't auto-play. Show poster + click trigger.
    if (this.saveData || this.reducedMotion || !this.autoplayEnabled) {
      this.renderPlayTrigger(slot);
      return;
    }

    this.injectVideo(slot, src, true);
  }

  renderPlayTrigger(slot) {
    // Already-rendered <img> stays as poster. Add a button overlay.
    if (slot.querySelector('.m-gallery__video-trigger')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'm-gallery__video-trigger';
    button.setAttribute('aria-label', slot.dataset.videoLabel || 'Video abspielen');
    button.innerHTML = `
      <span class="m-gallery__video-trigger-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
      </span>
    `;
    button.addEventListener('click', () => {
      this.injectVideo(slot, slot.dataset.videoSrc, true);
    });
    slot.appendChild(button);
  }

  injectVideo(slot, src, autoplay) {
    const video = document.createElement('video');
    video.src = src;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    if (autoplay) video.autoplay = true;
    video.setAttribute('aria-label', slot.dataset.videoLabel || 'Produkt-Video');
    slot.replaceChildren(video);
    if (autoplay) {
      // Some browsers require explicit play() call even with autoplay attr
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => { /* swallow autoplay-blocked errors */ });
      }
    }
  }

  unloadVideo(item) {
    if (item.dataset.mediaType !== 'video') return;
    const slot = item.querySelector('[data-video-slot]');
    if (!slot) return;
    const video = slot.querySelector('video');
    if (!video) return;

    // Pause + null out src so the browser releases the buffer.
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();

    // Restore poster image so the slot isn't empty if user ever re-activates.
    // (re-hydration in hydrateVideo() will create a fresh <video>.)
    const posterUrl = slot.dataset.videoPoster;
    if (posterUrl && !slot.querySelector('img')) {
      const img = document.createElement('img');
      img.src = posterUrl;
      img.className = 'm-gallery__media';
      img.alt = '';
      img.loading = 'lazy';
      slot.appendChild(img);
    }
  }
}

if (!customElements.get('minimalerei-gallery')) {
  customElements.define('minimalerei-gallery', MinimalereiGallery);
}
